/**
 * Print Service
 * Reusable print client for all doctypes
 * Uses JWT auth via Tenant Server (same pattern as tenantServerClient.ts)
 */

import { logger } from "@/lib/utils/logger";
import { AuthorizationError, NetworkError } from "@/lib/errors/ErrorTypes";
import { useAuthStore, ensureFreshToken } from "@/lib/store/authStore";
import { printLoadedFrame } from "./printInFrame";

export type PrintOptions = {
  format?: string;
  letterhead?: string;
  no_letterhead?: 0 | 1;
  language?: string;
  watermark?: string;
  company?: string;
};

/**
 * Optional: Hook for global error notifications
 * Set this if you have a global toast/notification system
 */
let globalErrorNotifier: ((message: string) => void) | null = null;

export function setPrintErrorNotifier(notifier: (message: string) => void) {
  globalErrorNotifier = notifier;
}

/**
 * Strip script tags from HTML (defense-in-depth)
 * Backend should already do this, but this adds extra safety for iframe preview
 */
function _stripScriptsFromHtml(html: string): string {
  // Remove <script> tags (including content)
  // This regex matches <script...>...</script> even with multiline content
  let cleaned = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

  // Remove script preload/prefetch links
  cleaned = cleaned.replace(/<link[^>]*(?:rel=["']preload["']|rel=["']prefetch["'])[^>]*(?:as=["']script["']|href=["'][^"']*website_script[^"']*["'])[^>]*>/gi, '');

  return cleaned;
}

/**
 * Build API URL for print endpoint using Tenant Server URL.
 * In CP auth mode, reads tenantServerUrl from auth store.
 * Falls back to env vars / window.location.origin for direct auth mode.
 */
function getPrintApiUrl(method: string): string {
  const { tenantServerUrl, authMode } = useAuthStore.getState();

  // CP auth mode: always use tenant server URL
  if (authMode === "control_plane" && tenantServerUrl) {
    return `${tenantServerUrl}/api/method/zaviago_backend.api.print.${method}`;
  }

  // Direct auth mode fallback (local dev, direct Frappe connection)
  const baseUrl = import.meta.env.VITE_FRAPPE_BASE_URL;
  const apiUrl = import.meta.env.VITE_FRAPPE_API_URL;
  const baseOrigin = baseUrl || (apiUrl?.replace(/\/api\/?$/, "")) || "";
  return `${baseOrigin}/api/method/zaviago_backend.api.print.${method}`;
}

/**
 * Get auth headers for print API requests.
 * Uses JWT Bearer token in CP mode, cookie auth in direct mode.
 */
function getPrintHeaders(): { headers: HeadersInit; credentials: RequestCredentials } {
  const { authMode, cpToken, currentTenant } = useAuthStore.getState();

  if (authMode === "control_plane" && cpToken) {
    return {
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "Authorization": `Bearer ${cpToken}`,
        "X-Tenant-ID": currentTenant || "",
      },
      credentials: "omit" as RequestCredentials,
    };
  }

  // Direct auth mode: use session cookies
  return {
    headers: {
      "Accept": "application/json",
    },
    credentials: "include" as RequestCredentials,
  };
}

/**
 * Get print preview HTML
 * Returns HTML string that can be rendered in an iframe
 */
export async function getPrintHtml(
  doctype: string,
  name: string,
  options: PrintOptions = {}
): Promise<string> {
  const {
    format = "Standard",
    letterhead,
    no_letterhead = 0,
    language,
    watermark,
    company,
  } = options;

  const params = new URLSearchParams({
    doctype,
    name,
    format,
    no_letterhead: String(no_letterhead),
    ...(letterhead ? { letterhead } : {}),
    ...(language ? { language } : {}),
    ...(watermark ? { watermark } : {}),
    ...(company ? { company } : {}),
  });

  try {
    // Ensure JWT token is fresh before making the request
    await ensureFreshToken();

    const url = `${getPrintApiUrl("preview")}?${params.toString()}`;
    const { headers, credentials } = getPrintHeaders();

    const res = await fetch(url, {
      method: "GET",
      credentials,
      headers,
    });

    if (!res.ok) {
      let errorMessage = "Failed to load print preview";
      try {
        const errorData = await res.json();
        let rawMessage = errorData.message || errorData.exc || errorMessage;

        if (Array.isArray(rawMessage)) {
          rawMessage = rawMessage[0] || rawMessage;
        }

        if (typeof rawMessage === "string") {
          errorMessage = rawMessage;
        } else {
          errorMessage = String(rawMessage);
        }

        if (res.status === 403 || errorMessage.toLowerCase().includes("permission")) {
          throw new AuthorizationError(
            errorMessage || "You do not have permission to print this document"
          );
        }
      } catch (parseError) {
        if (parseError instanceof AuthorizationError) throw parseError;
        if (res.status === 403) {
          throw new AuthorizationError("You do not have permission to print this document");
        }
      }

      throw new NetworkError(
        errorMessage || `Print preview request failed with status ${res.status}`
      );
    }

    // Frappe /api/method returns JSON - parse and extract HTML
    const data = await res.json();

    // Handle Frappe response wrapper:
    // - If we return {"html": "..."}, Frappe wraps it: {"message": {"html": "..."}}
    // - So we check: data.message.html
    // - Also handle direct: data.html or data.message (if message is HTML string)
    let html = data?.message?.html || data?.html || (typeof data?.message === "string" ? data.message : null);

    if (typeof html !== "string") {
      logger.warn("Unexpected response format from print preview, attempting text fallback");
      html = JSON.stringify(data);
    }

    // Defense-in-depth: strip any script tags that might have leaked through
    html = _stripScriptsFromHtml(html);

    return html;
  } catch (error) {
    // Re-throw known errors
    if (error instanceof AuthorizationError || error instanceof NetworkError) {
      throw error;
    }

    logger.error("Print preview request failed:", error);
    throw new NetworkError(
      error instanceof Error ? error.message : "Failed to load print preview"
    );
  }
}

/**
 * Download print PDF
 * Returns blob that can be downloaded
 */
export async function downloadPrintPdf(
  doctype: string,
  name: string,
  options: PrintOptions = {}
): Promise<Blob> {
  const {
    format = "Standard",
    letterhead,
    no_letterhead = 0,
    language,
    watermark,
    company,
  } = options;

  const params = new URLSearchParams({
    doctype,
    name,
    format,
    no_letterhead: String(no_letterhead),
    ...(letterhead ? { letterhead } : {}),
    ...(language ? { language } : {}),
    ...(watermark ? { watermark } : {}),
    ...(company ? { company } : {}),
  });

  try {
    await ensureFreshToken();

    const url = `${getPrintApiUrl("pdf")}?${params.toString()}`;
    const { headers, credentials } = getPrintHeaders();

    const res = await fetch(url, {
      method: "GET",
      credentials,
      headers,
    });

    if (!res.ok) {
      let errorMessage = "Failed to download PDF";
      try {
        const errorData = await res.json();
        let rawMessage = errorData.message || errorData.exc || errorMessage;

        if (Array.isArray(rawMessage)) {
          rawMessage = rawMessage[0] || rawMessage;
        }

        if (typeof rawMessage === "string") {
          if (rawMessage.includes("wkhtmltopdf") || rawMessage.includes("PDF generation is not configured")) {
            errorMessage = "PDF generation is not configured on this site. Please contact your system administrator.";
          } else {
            errorMessage = rawMessage;
          }
        } else {
          errorMessage = String(rawMessage);
        }

        if (res.status === 403 || errorMessage.toLowerCase().includes("permission")) {
          throw new AuthorizationError(
            errorMessage || "You do not have permission to print this document"
          );
        }
      } catch (parseError) {
        if (parseError instanceof AuthorizationError) throw parseError;
        if (res.status === 403) {
          throw new AuthorizationError("You do not have permission to print this document");
        }
        if (res.status === 417) {
          errorMessage = "PDF generation is not configured on this site. Please contact your system administrator.";
        }
      }

      throw new NetworkError(
        errorMessage || `PDF download request failed with status ${res.status}`
      );
    }

    return await res.blob();
  } catch (error) {
    // Re-throw known errors
    if (error instanceof AuthorizationError || error instanceof NetworkError) {
      throw error;
    }

    logger.error("PDF download request failed:", error);
    throw new NetworkError(
      error instanceof Error ? error.message : "Failed to download PDF"
    );
  }
}

/**
 * Print now (opens browser print dialog)
 * Legacy function - kept for backward compatibility
 */
export async function printNow(
  doctype: string,
  name: string,
  options?: PrintOptions
): Promise<void> {
  try {
    const blob = await downloadPrintPdf(doctype, name, options);

    const url = URL.createObjectURL(blob);

    // Create hidden iframe for printing
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.src = url;

    document.body.appendChild(iframe);

    let errorHandled = false;
    let loaded = false;

    iframe.onload = () => {
      loaded = true;
      setTimeout(() => {
        try {
          // Removed on the frame's afterprint (60s fallback), not after a fixed
          // delay: Safari returns from print() while the dialog is still open.
          void printLoadedFrame(iframe, () => URL.revokeObjectURL(url));
        } catch (printError) {
          // printLoadedFrame already removed the frame and revoked the URL.
          logger.error("Failed to trigger print dialog:", printError);
          errorHandled = true;

          if (globalErrorNotifier) {
            globalErrorNotifier("Failed to open print dialog. Please try again.");
          }
        }
      }, 100);
    };

    iframe.onerror = () => {
      if (!errorHandled) {
        errorHandled = true;
        logger.error("Failed to load PDF for printing");
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
        if (globalErrorNotifier) {
          globalErrorNotifier("Failed to load PDF for printing. Please try again.");
        }
      }
    };

    setTimeout(() => {
      if (!loaded && iframe.parentNode) {
        logger.warn("Print iframe did not load within timeout");
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }
    }, 10000);
  } catch (error) {
    logger.error("Print failed:", error);
    throw error;
  }
}

/**
 * Download as PDF (legacy function - kept for backward compatibility)
 */
export async function downloadPdf(
  doctype: string,
  name: string,
  options?: PrintOptions
): Promise<void> {
  try {
    const blob = await downloadPrintPdf(doctype, name, options);
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${doctype}-${name}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    logger.error("Download PDF failed:", error);
    throw error;
  }
}

/**
 * Get print configuration for a doctype
 * Uses JWT auth via Tenant Server (same as tenantServerClient.call)
 */
export async function getPrintConfig(doctype: string, company?: string) {
  try {
    await ensureFreshToken();

    const url = getPrintApiUrl("get_print_config");
    const { headers, credentials } = getPrintHeaders();

    const args: Record<string, string> = { doctype };
    if (company) args.company = company;

    const response = await fetch(url, {
      method: "POST",
      credentials,
      headers,
      body: JSON.stringify(args),
    });

    if (!response.ok) {
      throw new NetworkError(`Print config request failed with status ${response.status}`);
    }

    const result = await response.json();
    // Frappe wraps response in .message
    const data = result.message || result;

    return {
      formats: data.formats || [],
      default_format: data.default_format || "Standard",
      profiles: data.profiles || [],
      languages: data.languages || [],
      default_language: data.default_language || "en",
    };
  } catch (error) {
    logger.error("Failed to get print config:", error);
    throw error;
  }
}


// ---------------------------------------------------------------------------
// Printable documents hub
// ---------------------------------------------------------------------------

export type LinkedDocument = {
  doctype: string;
  name: string;
  status: string;
  date: string | null;
  label: string;
  /**
   * Explicit print format to render for this entry. When present the print
   * page opens directly on this format (key documents such as Tax Invoice /
   * Quotation). Absent for related documents, which auto-select their format.
   */
  print_format?: string;
};

export type PrintableDocumentsResult = {
  sales_order: { name: string; status: string; date: string | null };
  /**
   * Labelled one-click key documents (ใบกำกับภาษี / ใบเสนอราคา / ใบเสร็จ),
   * each carrying an explicit print_format.
   */
  key_documents: LinkedDocument[];
  linked: LinkedDocument[];
};

/**
 * Fetch all printable documents linked to a Sales Order.
 * Returns the SO itself + any Sales Invoices, Delivery Notes,
 * Payment Entries, and Pick Lists.
 */
export async function getPrintableDocuments(
  salesOrder: string
): Promise<PrintableDocumentsResult> {
  try {
    await ensureFreshToken();

    const url = getPrintApiUrl("get_printable_documents");
    const { headers, credentials } = getPrintHeaders();

    const response = await fetch(url, {
      method: "POST",
      credentials,
      headers,
      body: JSON.stringify({ sales_order: salesOrder }),
    });

    if (!response.ok) {
      throw new NetworkError(
        `Printable documents request failed with status ${response.status}`
      );
    }

    const result = await response.json();
    const data = result.message || result;

    return {
      sales_order: data.sales_order || { name: salesOrder, status: "", date: null },
      key_documents: Array.isArray(data.key_documents) ? data.key_documents : [],
      linked: Array.isArray(data.linked) ? data.linked : [],
    };
  } catch (error) {
    logger.error("Failed to get printable documents:", error);
    throw error;
  }
}
